import { Bell, LoaderCircle } from "lucide-react";
import { Button } from "@facade/components/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import type { NotificationFeedProps } from "./NotificationFeed.types";
import { NotificationItem } from "../NotificationItem";
import { cn } from "@facade/lib/utils";
import { notificationQueryKeys } from "../../queries";
import { notificationsApi } from "@facade/services";
import { projectsQueries } from "@facade/features/projects/queries";
import { useMemo } from "react";

export function NotificationFeed({
  params,
  enabled = true,
  showProjectNames = false,
  onNotificationSelect,
  emptyTitle = "No notifications yet",
  emptyDescription = "New activity will appear here.",
  className,
}: NotificationFeedProps) {
  const notificationsQuery = useInfiniteQuery({
    queryKey: notificationQueryKeys.list(params),
    queryFn: ({ pageParam }) =>
      notificationsApi.getAll({ ...params, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled,
  });
  const notifications =
    notificationsQuery.data?.pages.flatMap((page) => page.notifications) ?? [];
  const hasProjectNotifications = notifications.some(
    (notification) => Boolean(notification.projectId),
  );
  const projectsQuery = useQuery({
    ...projectsQueries.list(),
    enabled: enabled && showProjectNames && hasProjectNotifications,
  });
  const projectNamesById = useMemo(
    () =>
      new Map(
        (projectsQuery.data ?? []).map((project) => [project.id, project.name]),
      ),
    [projectsQuery.data],
  );

  return (
    <div className={cn("min-h-0", className)}>
      {notificationsQuery.isPending ? (
        <div className="grid min-h-40 place-items-center">
          <LoaderCircle className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="grid min-h-48 place-items-center px-6 text-center">
          <div>
            <span className="mx-auto mb-3 grid size-10 place-items-center rounded-full border">
              <Bell className="size-5 text-muted-foreground" />
            </span>
            <p className="text-sm font-medium">{emptyTitle}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {emptyDescription}
            </p>
          </div>
        </div>
      ) : (
        <>
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              projectName={
                showProjectNames && notification.projectId
                  ? projectNamesById.get(notification.projectId)
                  : undefined
              }
              onSelect={onNotificationSelect}
            />
          ))}

          {notificationsQuery.hasNextPage ? (
            <div className="p-3 text-center">
              <Button
                variant="ghost"
                size="sm"
                disabled={notificationsQuery.isFetchingNextPage}
                onClick={() => void notificationsQuery.fetchNextPage()}
              >
                {notificationsQuery.isFetchingNextPage ? (
                  <LoaderCircle className="animate-spin" />
                ) : null}
                Load more
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
